<template>
  <div id="insider-backtester-transactions-filter-tab">
    <v-container fluid pa-0>
      <v-row>
        <!-- Transaction Types -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header class="mb-2"> Transaction Types </helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            <strong>All Buy transactions</strong> includes every transaction coded as a purchase in
            SEC filings — including exercises of options, conversions, and DRIP (dividend
            reinvestment). <strong>Open Market purchases</strong> restricts to cash purchases made
            on the open market, which is the cleanest conviction signal since the insider is spending
            their own money at market price.
          </p>
          <v-radio-group v-model="clusterCriteria.transaction_type" row hide-details>
            <v-radio :value="1" label="All Buy transactions" color="primary" />
            <v-radio :value="4" label="Open Market purchases only (recommended)" color="primary" />
          </v-radio-group>
          <v-checkbox
            v-model="clusterCriteria.open_market_purchase_code"
            density="compact"
            :disabled="clusterCriteria.transaction_type != 4"
            label="Exclude purchases made through shareholder purchase agreements or similar arrangements"
            hint="Removes open market purchases made through pre-arranged shareholder agreements rather than discretionary buying"
            persistent-hint
            class="mt-0"
          />
          <v-checkbox
            v-model="clusterCriteria.is_10b5"
            density="compact"
            label="Include purchases made under a 10b5-1 plan (excluded by default)"
            hint="10b5-1 plans are pre-scheduled trading agreements set up months in advance. Including them reduces the spontaneity of the signal."
            persistent-hint
            class="mt-0"
          />
          <p class="text-body-2 mt-2 mb-1">
            <strong>Direct</strong> ownership means the insider holds shares personally.
            <strong>Indirect</strong> ownership is through a trust, LLC, or family member. Most
            analysts focus on direct purchases as the purest insider conviction signal.
          </p>
          <v-radio-group v-model="clusterCriteria.direct_owner" row hide-details>
            <v-radio :value="0" label="Direct or Indirect transactions" color="primary" />
            <v-radio :value="1" label="Direct owner transactions only" color="primary" />
            <v-radio :value="2" label="Indirect owner transactions only" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- Date Range -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header>Date Range</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            The lookback window for counting insider transactions that form a cluster. A tighter
            window (1–3 days) requires insiders to buy in near-synchrony. A wider window (7–14 days)
            catches broader accumulation campaigns.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-select
                v-model="clusterCriteria.date_range"
                :items="dateRangeItems"
                item-title="text"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <p class="text-body-2 mb-1">
            <strong>Individual Stock Cluster Buying</strong> fires a signal when multiple insiders
            at the same company buy that company's stock within the date window — a strong signal
            that management has consensus conviction. <strong>Sector/Industry Cluster Buying</strong>
            fires when insiders across multiple companies in the same sector are all buying —
            useful for identifying macro rotations into a sector before they become widely known.
          </p>
          <v-radio-group v-model="clusterCriteria.industry_cluster" row hide-details>
            <v-radio :value="false" label="Individual Stock Cluster Buying" color="primary" />
            <v-radio :value="true" label="Sector/Industry Cluster Buying" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- Types of Owners -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header class="mb-2"> Types of Owners </helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Filter by the role of the insider making the purchase. <strong>C-Level Officers</strong>
            (CEO, CFO, COO) tend to have the strongest informational edge.
            <strong>Directors</strong> are also well-informed. <strong>&gt;10% owners</strong> may
            be strategic investors with their own thesis. Select multiple types to combine them.
          </p>
          <v-select
            v-model="clusterCriteria.owner_types"
            :items="ownerTypes"
            item-title="text"
            variant="outlined"
            density="compact"
            multiple
            chips
            closable-chips
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <!-- Minimum # insiders -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Minimum # Insiders</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            Require at least this many unique insiders to buy before the signal fires. Setting 2+
            filters for genuine cluster conviction rather than a single insider's trade.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_unique_owners"
                mask="####"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Min. Cluster Amount -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Min. Cluster Amount</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            Total dollar value of all insider purchases in the cluster combined. Filters out token
            purchases. A typical starting point is $50,000–$250,000.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_transaction_amount"
                type="number"
                prefix="$"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Minimum # transactions -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Minimum # Transactions</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            Total number of individual buy transactions (one insider buying on 3 separate days = 3
            transactions). Use alongside Minimum # Insiders to tune signal sensitivity.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_transaction_count"
                mask="####"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Minimum # stocks:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_stock_count"
                mask="####"
                variant="outlined"
                density="compact"
                persistent-hint
                hint="(used with sector/industry cluster buying)"
                :disabled="!clusterCriteria.industry_cluster"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Min. Cluster Amount -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Min. % of Insider's Total Shares</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            The purchase must represent at least this % of the insider's existing holdings. Filters
            for high-conviction buys where the insider is meaningfully increasing their stake.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_percent_insider_shares"
                type="number"
                suffix="%"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Min. Cluster Amount -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Max. % of Insider's Total Shares</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            Optional upper limit. Can be used to exclude cases where the purchase is part of a
            large initial stake acquisition rather than an incremental add to an existing position.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.maximum_percent_insider_shares"
                type="number"
                suffix="%"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <helpers-section-sub-header class="mb-2">Filing Timing Mode</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Controls when intraday SEC filings are considered available. <strong>Previous day /
            pre-open</strong> is the conservative, realistic setting — the backtester only acts on
            filings from the prior day or those received before the 9:30AM open. <strong>Real-time
            mode</strong> assumes you can see and act on filings filed any time up to 3:30PM on the
            same trading day — slightly optimistic but useful for testing intraday strategies.
          </p>
          <v-radio-group v-model="criteria.real_time_mode">
            <v-radio
              :value="1"
              label="Previous day or pre-open filings only (realistic / conservative)"
            />
            <v-radio
              :value="2"
              label="Real-time: filings received any time before 3:30PM EST are eligible same day"
            />
          </v-radio-group>
        </v-col>
        <!-- Trading Delay -->
        <v-col v-if="1 == 2" cols="12" mb-2>
          <helpers-section-sub-header>Trading delay:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="criteria.trading_delay"
                type="number"
                suffix="days"
                variant="outlined"
                density="compact"
                hint="Days after filing(s) released before being considered for backtesting"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Restrict to Buy Reversals Only -->
        <v-col cols="12">
          <v-checkbox
            v-model="clusterCriteria.buy_reversal"
            density="compact"
            label="Restrict to Buy reversals only"
            hint="Checking the buy reversal option will limit transactions to only those where an insider purchases new shares after having only sold shares during the past 18 months."
            persistent-hint
            class="mt-0"
          />
        </v-col>
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Insider Past Performance and Unusual Trades
          </helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Filter for insiders with a demonstrated track record of profitable buys, or for
            situations where the insider's buying is unusually large relative to their history.
            Performance fields compare the average return of the insider's past purchases at each
            time horizon — useful for focusing on insiders whose buys have historically been
            predictive. <strong>Min. # of days since last buy</strong> requires a gap between this
            purchase and the insider's previous buy, filtering out habitual/routine buyers in favor
            of more significant one-off purchases.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_1d_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 1d Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_5d_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 5d Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>

            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_1m_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 1m Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_6m_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 6m Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_1y_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 1y Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_past_trades"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. # of Past Trades"
                class="pt-0"
                style="max-width: 200px"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.max_past_trades"
                type="number"
                variant="outlined"
                density="compact"
                label="Max. # of Past Trades"
                class="pt-0"
                style="max-width: 200px"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="3">
              <v-text-field
                v-model="clusterCriteria.days_since_last_buy"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. # of days since last buy"
                class="pt-0"
                style="max-width: 250px"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
// Modules
import { mapStoreState } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { mapWritableState } from 'pinia'

export default {
  data() {
    return {
      dateRangeItems: [
        { value: 0, text: 'Past Day' },
        { value: 1, text: 'Past 3 Days' },
        { value: 2, text: 'Past 7 Days' },
        { value: 6, text: 'Past 2 Weeks' },
        { value: 3, text: 'Past Month' } /*,
        { value: 4, text: 'Past 3 Months' } */
      ],
      ownerTypes: [
        { value: 1, text: '>10% Owner' },
        { value: 2, text: 'Director' },
        { value: 3, text: 'Officer' },
        { value: 5, text: 'C-Level Officer (CEO, COO, CFO, etc.)' },
        { value: 4, text: 'Other Owner' }
      ],
      marketCaps: [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$250 million - $2 billion', id: 7 },
        { name: 'Below $250 million', id: 8 }
      ]
    }
  },

  computed: {
    ...mapStoreState('ww', ['sectors']),
    ...mapWritableState(useAppStore, ['whaleTraderCriteria', 'signalView', 'exit_time_periods']),
    criteria() {
      return this.whaleTraderCriteria.signals.insider_signal
    },
    appcriteria() {
      return this.whaleTraderCriteria
    },
    clusterCriteria() {
      return this.whaleTraderCriteria.signals.insider_signal.entry_cluster_buy_criteria
    },
    enabled() {
      return this.whaleTraderCriteria.signals.insider_signal.enabled
    }
    /*...mapFields({
      clusterCriteria: 'app.whaleTraderCriteria.signals.insider_signal.entry_cluster_buy_criteria',
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.insider_signal',
      enabled: 'app.whaleTraderCriteria.signals.insider_signal.enabled'
    })*/
  },

  mounted() {
    useWwStore().loadSectors()
    if (this.clusterCriteria) {
      if (typeof this.clusterCriteria.industry_cluster == 'undefined') {
        this.clusterCriteria.industry_cluster = false
      }
    }
  }
}
</script>
